<template>
  <div class="summary-bar-success" :class="{ 'is-mobile': !isDesktop }">
    <div class="summary-bar-success__card">
      <div class="summary-bar-success__top">
        <div class="summary-bar-success__confirmation">
          <div class="confirmation-text">
            <ClientOnly>
              <Vue3Lottie
                :animation-link="confirmationLottieUrl"
                :height="32"
                :width="32"
                :loop="false"
                :auto-play="true"
                class="confirmation-animation"
              />
              <template #fallback>
                <div class="confirmation-animation-placeholder"></div>
              </template>
            </ClientOnly>
            <span class="large confirmation-text-label">Added to bag ({{ addedItemsCount }})</span>
          </div>
          <button v-if="!isDesktop" class="close-btn" aria-label="Close success message" @click="$emit('close')">
            <Icon :icon="csgClose" size="m" />
          </button>
        </div>
        <div v-if="isError" class="summary-bar-success__error" data-testid="summary-bar-success-error">Not all items were added to your bag.</div>
      </div>

      <div class="carousel-wrapper" data-testid="summary-bar-success-carousel">
        <SummaryBarCarousel :list="items" step-by-step>
          <template #item="{ data: item }">
            <div class="thumb-wrapper" data-testid="summary-bar-thumbnail-item">
              <Picture
                v-if="item.imageUrl"
                :key="item.upcId"
                :lazy-load="false"
                class="thumb-image"
                :image-url="item.imageUrl"
                :alt="item.alt || ''"
                :image-sizes-by-breakpoints="{ small: 110, medium: 110, large: 110, mlarge: 110, xlarge: 110 }"
              />
              <SummaryBarItemBadge :quantity="item.quantity" :error-message="failedUpcs?.[item.upcId]?.[0]" />
            </div>
          </template>
        </SummaryBarCarousel>
      </div>

      <div class="summary-bar-success__math">
        <div class="summary-bar-success__subtotal h6">
          <span>Pre-tax order total ({{ lastBagSummary?.itemCount ?? 0 }})</span>
          <span>{{ formatCurrency(lastBagSummary?.merchandiseTotal ?? 0) }}</span>
        </div>

        <div v-if="savings > 0" class="summary-bar-success__savings">
          <span data-testid="savings-label">You save {{ savingsInPercents }}%</span>
          <span data-testid="savings-amount">{{ formatCurrency(savings) }}</span>
        </div>
      </div>

      <div class="summary-bar-success__actions">
        <Button
          variant="emphasis"
          expanded
          class="checkout-button"
          :is-loading="isCheckoutLoading"
          :disabled="isCheckoutLoading"
          @click="handleCheckout"
        >
          Checkout
        </Button>
        <Button variant="secondary" expanded :disabled="isCheckoutLoading" @click="handleViewBag"> View bag </Button>
      </div>
    </div>

    <Button v-if="isDesktop" class="link-body" :variant="'link'" @click="$emit('add-more')"> Add more items </Button>
  </div>
</template>

<script setup lang="ts">
import { brand } from 'config';
import { computed, ref, defineAsyncComponent } from 'vue';
import { storeToRefs } from 'pinia';
import Button from '@atomic-ui/button/Button.vue';
import Icon from '@atomic-ui/icon/Icon.vue';
import Picture from '@atomic-ui/picture/Picture';
import { csgClose } from '@csgSvgIcons/icons';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useMultiATBStore } from '~/stores/useMultiATBStore';
import SummaryBarCarousel from './SummaryBarCarousel.vue';
import SummaryBarItemBadge from './SummaryBarItemBadge.vue';
import formatCurrency from '@nuxt-common/media-card-large/app/utils/currencyFormatter';

import { REDIRECT_MAP } from '~/constants/common';
import { asyncNavigation } from '~/utils/navigation/asyncNavigation';
import { ExpressCheckoutService } from '~/modules/add-to-bag/services/ExpressCheckout.service';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';

const Vue3Lottie = defineAsyncComponent(() => import('vue3-lottie').then((m) => m.Vue3Lottie));

const { isDesktop } = useDeviceType();

const { cdnURL = '' } = useRuntimeConfig().app || {};
const lottiePath = `/animations/${brand}_confirmation_icon.json`;
const confirmationLottieUrl = cdnURL + lottiePath;

const multiATBStore = useMultiATBStore();
const { lastSubmittedItems: items, addedItemsCount, failedUpcs, savings, savingsInPercents, lastBagSummary } = storeToRefs(multiATBStore);

const isError = computed(() => Object.keys(failedUpcs.value || {}).length > 0);

const {
  killswitches: { isStartCheckoutPostCallEnabled },
} = useKillswitches();
const expressCheckoutService = new ExpressCheckoutService({ isStartCheckoutPostCallEnabled });

const isCheckoutLoading = ref(false);

const handleCheckout = async () => {
  isCheckoutLoading.value = true;
  try {
    await expressCheckoutService.checkout();
  } finally {
    isCheckoutLoading.value = false;
  }
};

const handleViewBag = async () => {
  await asyncNavigation(REDIRECT_MAP.bag);
};

defineEmits<(e: 'add-more' | 'close') => void>();
</script>






<style lang="scss" scoped>
@import '~settingSCSS';

.summary-bar-success {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .summary-bar-success__confirmation {
    .confirmation-text {
      position: relative;

      .confirmation-animation,
      .confirmation-animation-placeholder {
        position: absolute;
        width: rem-calc(37);
        height: rem-calc(37);
        left: -5px;
        top: -9px;
      }

      .confirmation-text-label {
        @include margin-left-m;
      }
    }
  }

  &.is-mobile {
    gap: 0;
    overflow: hidden;

    .summary-bar-success__card {
      border: none;
      @include padding-z;
    }

    .summary-bar-success__confirmation {
      justify-content: space-between;
      width: 100%;

      .close-btn {
        background: transparent;
        border: none;
        padding: 0;
        cursor: pointer;
        display: flex;
        align-items: center;
      }
    }
  }
}

.summary-bar-success__card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  @include padding-xs();
  border: 1px solid $gray-5-color;
  background: $white;
  transition: gap 0.3s ease;
}

.summary-bar-success__confirmation {
  display: flex;
  align-items: center;
  gap: 8px;
  @include margin-bottom-z;
  transition: margin-bottom 0.3s ease;
}

.summary-bar-success__error {
  color: $error-color;
  margin-top: rem-calc(4);
}

.carousel-wrapper {
  margin-left: rem-calc(-16);
  margin-right: rem-calc(-16);
  max-width: 100vw;

  &:deep(.carousel-slide-button) {
    cursor: auto;
  }
}

.thumb-wrapper {
  position: relative;
  width: rem-calc(110);
  height: rem-calc(137);
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumb-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
}

.summary-bar-success__math {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-height: 200px;
  opacity: 1;
  transition:
    max-height 0.3s ease,
    visibility 0.3s ease,
    opacity 0.3s ease;
}
.summary-bar-success__subtotal {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-bar-success__savings {
  color: $sale-color;
  display: flex;
  justify-content: space-between;
}

.summary-bar-success__actions {
  display: flex;
  flex-direction: row;
  gap: rem-calc(12);
  width: 100%;

  .checkout-button {
    order: 1;
  }

  @include breakpoint(medium up) {
    flex-direction: column;
    gap: rem-calc(16);

    .checkout-button {
      order: 0;
    }
  }
}


@import '~settingSCSS';

.summary-bar-success__savings {
  color: $black;
}
</style>